if (!customElements.get('video-player')) {
  const TOGGLE_PAIRS = { play: 'pause', pause: 'play', mute: 'unmute', unmute: 'mute' };

  class VideoPlayer extends HTMLElement {
    connectedCallback() {
      this._init();

      const handleAction = (e) => {
        const clickedElement = e.target.closest('[data-action]');
        if (!clickedElement) return;

        const { action } = clickedElement.dataset;

        switch (action) {
          case 'play':
            this.video.play();
            this.setAttribute('data-state', 'playing');
            break;
          case 'pause':
            this.video.pause();
            this.setAttribute('data-state', 'paused');
            break;
          case 'mute':
            this.video.muted = true;
            this.setAttribute('data-muted', '');
            break;
          case 'unmute':
            this.video.muted = false;
            this.removeAttribute('data-muted');
            break;
        }

        // ADA: the clicked button hides itself, so hand keyboard focus to its counterpart
        const counterpart = TOGGLE_PAIRS[action];
        if (counterpart && clickedElement === document.activeElement) {
          const next = clickedElement.parentElement?.querySelector(`[data-action="${counterpart}"]`);
          if (next) requestAnimationFrame(() => next.focus());
        }
      };

      this.addEventListener('click', handleAction);

      const externalControls = this.parentElement?.querySelector('[data-video-controls]');
      if (externalControls) {
        externalControls.addEventListener('click', handleAction);

        ['data-state', 'data-muted'].forEach((attr) => {
          const val = this.getAttribute(attr);
          if (val === null) externalControls.removeAttribute(attr);
          else externalControls.setAttribute(attr, val);
        });

        new MutationObserver((mutations) => {
          for (const m of mutations) {
            const val = this.getAttribute(m.attributeName);
            if (val === null) externalControls.removeAttribute(m.attributeName);
            else externalControls.setAttribute(m.attributeName, val);
          }
        }).observe(this, { attributes: true, attributeFilter: ['data-state', 'data-muted'] });
      }
    }

    _getActiveVideo() {
      const videos = this.querySelectorAll('video');
      for (const v of videos) {
        if (v.offsetParent !== null) return v;
      }
      return videos[0] || null;
    }

    _init() {
      this.video = this._getActiveVideo();
      if (!this.video) return;

      this.querySelectorAll('video').forEach(v => v.setAttribute('aria-hidden', 'true'));
      this.video.querySelector('img')?.setAttribute('alt', '');

      // Keep the sound toggle in sync with the real audio state (videos render muted)
      this.toggleAttribute('data-muted', this.video.muted);

      const progressBarInner = this.querySelector('[data-progress-bar-inner]');
      const circularProgressBar = this.querySelector('[data-circular-progress-bar]');
      const radius = circularProgressBar?.r.baseVal.value;
      const circumference = radius ? 2 * Math.PI * radius : 0;

      if (circularProgressBar) {
        circularProgressBar.style.strokeDasharray = `${circumference}`;
        circularProgressBar.style.strokeDashoffset = `${circumference}`;
      }

      this.video.addEventListener('timeupdate', () => {
        const curr = (this.video.currentTime / this.video.duration) * 100;
        if (progressBarInner) progressBarInner.style.width = `${curr}%`;
        if (circularProgressBar) {
          circularProgressBar.style.strokeDashoffset = `${circumference - (curr / 100) * circumference}`;
        }
      });

      this.video.addEventListener('ended', () => {
        this.setAttribute('data-state', 'ended');
        this.video.pause();
        this.video.load();
        if (progressBarInner) progressBarInner.style.width = null;
        if (circularProgressBar) circularProgressBar.style.strokeDashoffset = `${circumference}`;
      });

      if (this.dataset.autoplay === 'true') {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.video.style.opacity = '1';
          this.setAttribute('data-state', 'paused');
        } else {
          this.video.style.opacity = '0';
          this._autoplay();
        }
      }
    }

    _fadeIn() {
      this.video.style.transition = 'opacity 300ms ease';
      this.video.style.opacity = '1';
    }

    _autoplay() {
      this.video.muted = true;
      this.setAttribute('data-muted', '');
      this.video.playsInline = true;

      // Fallback: if play never resolves (Low Power Mode, Safari autoplay block), reveal after 4s
      const fallbackTimer = setTimeout(() => this._fadeIn(), 4000);
      const done = () => clearTimeout(fallbackTimer);

      const attempt = () => {
        this.video.play().then(() => {
          done();
          this.setAttribute('data-state', 'playing');
          this._fadeIn();
        }).catch(() => {
          this.video.addEventListener('canplay', () => {
            this.video.play().then(() => {
              done();
              this.setAttribute('data-state', 'playing');
              this._fadeIn();
            }).catch(() => {
              done();
              this.setAttribute('data-state', 'paused');
              this._fadeIn();
            });
          }, { once: true });
        });
      };

      if (this.video.readyState < 1) {
        this.video.addEventListener('loadedmetadata', attempt, { once: true });
        this.video.load();
      } else {
        attempt();
      }
    }
  }

  customElements.define('video-player', VideoPlayer);
}
